import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';



// Asset Card Component

const AssetCard = ({ asset, onEdit, onDelete, onClick }) => {
    // Use backend-calculated condition rating and AI-estimated circularity score
    const conditionRating = Number(asset.condition_rating || 0);
    const circularityScore = Number(asset.circularity_score || 0);
    const aiRecommendedRStrategy = asset.ai_recommended_r_strategy;
    const aiRecommendedEOLStrategy = asset.ai_recommended_end_of_life_strategy;

    // Map condition rating to text and color (0-5 scale)
    let condition, conditionColor;
    if (conditionRating >= 4.0) {
        condition = 'Excellent';
        conditionColor = 'text-green-500';
    } else if (conditionRating >= 3.0) {
        condition = 'Good';
        conditionColor = 'text-blue-500';
    } else if (conditionRating >= 2.0) {
        condition = 'Fair';
        conditionColor = 'text-yellow-500';
    } else if (conditionRating >= 1.0) {
        condition = 'Poor';
        conditionColor = 'text-orange-500';
    } else {
        condition = 'Critical';
        conditionColor = 'text-red-500';
    }

    return (
        <div 
            onClick={(e) => {
                e.stopPropagation();
                if (onClick) onClick(asset.id);
            }}
            className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer h-full flex flex-col"
        >
            <div className="p-5 flex-1 flex flex-col">
                <div className="flex justify-between items-start mb-3">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-800">{asset.name}</h3>
                        <p className="text-sm text-gray-500">ID: {asset.id}</p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800 border border-gray-200" title="Current user-selected strategy">
                            {asset.current_strategy || '—'}
                        </span>
                        <span className="text-[11px] text-gray-400">vs</span>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full border ${asset.ai_recommended_r_strategy ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-400 border-gray-200'}`}
                          title={asset.ai_recommended_r_strategy ? 'AI-recommended R strategy' : 'No AI recommendation yet'}
                        >
                          {asset.ai_recommended_r_strategy || 'AI —'}
                        </span>
                    </div>
                </div>

                <div className="mt-2 flex items-center">
                    <div className="flex-1">
                        <p className="text-sm text-gray-600">Condition</p>
                        <div className="flex items-center">
                            <div className={`w-2 h-2 rounded-full mr-2 ${conditionColor}`}></div>
                            <span className="text-sm font-medium">{condition} ({conditionRating.toFixed(1)}/5.0)</span>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm text-gray-600">Value</p>
                        <p className="text-sm font-semibold">${Number(asset.current_market_value || 0).toLocaleString()}</p>
                    </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex justify-between items-center mb-2">
                        <span className="text-sm text-gray-600">Circularity Score</span>
                        <div className="text-right">
                            <span className="text-sm font-medium">{circularityScore.toFixed(1)}%</span>
                            {asset.ai_analysis_timestamp && (
                                <div className="text-xs text-blue-600">AI Estimated</div>
                            )}
                        </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-green-500 h-2 rounded-full"
                            style={{ width: `${circularityScore}%` }}
                        ></div>
                    </div>

                    {/* AI Strategy Recommendations */}
                    {(aiRecommendedRStrategy || aiRecommendedEOLStrategy) && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="text-xs font-medium text-gray-700 mb-2">AI Recommendations:</div>
                            {aiRecommendedRStrategy && (
                                <div className="flex justify-between items-center mb-1">
                                    <span className="text-xs text-gray-600">R Strategy:</span>
                                    <span className="text-xs font-medium text-blue-600">{aiRecommendedRStrategy}</span>
                                </div>
                            )}
                            {aiRecommendedEOLStrategy && (
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-gray-600">End-of-Life:</span>
                                    <span className="text-xs font-medium text-green-600 truncate ml-1" title={aiRecommendedEOLStrategy}>
                                        {aiRecommendedEOLStrategy.length > 15 ? `${aiRecommendedEOLStrategy.substring(0, 15)}...` : aiRecommendedEOLStrategy}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="mt-3 text-xs text-gray-500 space-y-1">
                    <p>Age: {asset.age_in_months} months</p>
                    <p>Life Expectancy: {asset.life_expectancy_in_months} months</p>
                    <div className="mt-1">
                        <p className="font-medium text-gray-700 mb-1">Material Composition:</p>
                        {Array.isArray(asset.material_components) && asset.material_components.length > 0 ? (
                            <div className="space-y-1">
                                {asset.material_components.map((material, index) => {
                                    const percentage = material.percentage || 0;
                                    return (
                                        <div key={index} className="flex items-center">
                                            <span className="w-24 truncate" title={material.name || material}>
                                                {material.name || material}:
                                            </span>
                                            <div className="flex-1 ml-2 bg-gray-200 rounded-full h-2">
                                                <div
                                                    className="bg-blue-500 h-2 rounded-full"
                                                    style={{ width: `${percentage}%` }}
                                                />
                                            </div>
                                            <span className="ml-2 w-10 text-right font-medium">
                                                {percentage.toFixed(1)}%
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="truncate" title={asset.material_components}>
                                {asset.material_components}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="bg-gray-50 px-5 py-3 flex justify-end space-x-2">
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        onEdit(asset);
                    }}
                    className="p-2 text-gray-500 hover:text-blue-600 transition"
                >
                    <Pencil className="w-4 h-4" />
                </button>
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(asset.id);
                    }}
                    className="p-2 text-gray-500 hover:text-red-600 transition"
                >
                    <Trash2 className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
};  

export default AssetCard;
